import LaunchIcon from "@mui/icons-material/Launch";

import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useNotifications } from "@toolpad/core";
import { useEffect, useState } from "react";
import CustomTable from "../components/CustomTable";
import { getPdfFaktura } from "../services/pdfService";
import { fetchAvailableYears, fetchZalohy } from "../services/zalohyService";
import { formatCurrency } from "../utils/currencyUtils";
import { formatDate } from "../utils/dateUtils";
import { handleApiError } from "../utils/errorHandler";

export default function PrehledZaloh() {
  const currentYear = new Date().getFullYear();

  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedLimit, setSelectedLimit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [zalohy, setZalohy] = useState([]);
  const [error, setError] = useState(null);
  const notifications = useNotifications();

  useEffect(() => {
    async function fetchYears() {
      const result = await fetchAvailableYears();
      if (result.success) {
        const data = result.data;

        if (!data.includes(currentYear)) {
          if (data.length === 0) {
            return;
          }
          setAvailableYears([currentYear, ...data.reverse()]);
        } else {
          setAvailableYears(data.reverse());
        }
      } else {
        setError(handleApiError(result));
      }
    }

    fetchYears();
  }, [currentYear]);

  // Načteme zálohy, buď za zvolený rok, nebo posledních selectedLimit
  useEffect(() => {
    setLoading(true);
    async function fetchData() {
      const result = await fetchZalohy(selectedYear, selectedLimit);
      if (result.success) {
        setZalohy(result.data);
      } else {
        setError(handleApiError(result));
      }
      setLoading(false);
    }
    fetchData();
  }, [selectedYear, selectedLimit]);

  const handleCisloFakturyClick = async (cisloFaktury) => {
    const result = await getPdfFaktura(cisloFaktury);

    if (!result.success) {
      if (result.status === 404) {
        notifications.show(`PDF faktura č. ${cisloFaktury} nebyla nalezena.`, {
          severity: "error",
          autoHideDuration: 5000,
        });
        return;
      }
      const customError = new Error(result.message || "Neznámá chyba");
      customError.status = result.status;
      setError(customError);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    throw new Response(error.message, { status: error.status });
  }

  const columns = [
    {
      id: "datumUhrady",
      label: "Datum",
      renderCell: (value, row) =>
        row?.datumUhrady ? formatDate(row.datumUhrady) : "-",
    },
    {
      id: "castka",
      label: "Částka",
      renderCell: (value) => (value ? formatCurrency(value) : "-"),
    },
    {
      id: "cisloFaktury",
      label: "Faktura",
      renderCell: (value, row) =>
        row.cisloFaktury ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>{row.cisloFaktury}</Typography>
            <LaunchIcon
              color="primary"
              sx={{ cursor: "pointer", marginLeft: "10px" }}
              onClick={() => handleCisloFakturyClick(row.cisloFaktury)}
            />
          </Box>
        ) : (
          "-"
        ),
    },
    {
      id: "poznamka",
      label: "Poznámka",
    },
  ];

  return (
    <>
      {zalohy?.length > 0 ? (
        <>
          <Box sx={{ mb: 2, textAlign: "center" }}>
            {availableYears?.length > 0 && (
              <>
                <FormControl sx={{ minWidth: "180px" }}>
                  <InputLabel id="yearSelector">Zobrazit za rok</InputLabel>
                  <Select
                    labelId="yearSelector"
                    id="yearSelector"
                    value={selectedYear}
                    label="Zobrazit za rok"
                    onChange={(event) => setSelectedYear(event.target.value)}
                  >
                    <MenuItem
                      value=""
                      sx={{
                        // height: "20px",
                        textAlign: "center",
                        justifyContent: "center",
                      }}
                    >
                      -
                    </MenuItem>
                    {[...availableYears].map((year) => (
                      <MenuItem
                        value={year}
                        key={year}
                        sx={{
                          // height: "20px",
                          textAlign: "center",
                          justifyContent: "center",
                        }}
                      >
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>

          <Typography
            variant="h6"
            sx={{
              mb: 2,
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            {selectedYear ? (
              `Zálohy za ${selectedYear}`
            ) : (
              <>
                Posledních
                <FormControl size="small" sx={{ minWidth: 60 }}>
                  <Select
                    value={selectedLimit}
                    onChange={(e) => setSelectedLimit(e.target.value)}
                    sx={{ fontSize: "inherit", ml: 1, mr: 1 }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </Typography>
          <CustomTable columns={columns} data={zalohy} />
        </>
      ) : (
        <>
          {availableYears?.length > 0 ? (
            <Box sx={{ textAlign: "center", my: 4 }}>
              <Typography variant="body1">
                Nemáte žádnou zálohu za tento rok.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", my: 4 }}>
              <Typography variant="body1">Nemáte žádné zálohy.</Typography>
            </Box>
          )}
        </>
      )}
    </>
  );
}
